import type { ReactNode } from "react";
import { FormControl, FormHelperText } from "@mui/material";
import type { FieldValues, FieldPath } from "react-hook-form";
import { useController } from "react-hook-form";
import type { ControlledInput } from "utils";
import { PerimetreAutoCompleteWithErrorBoundary } from ".";

interface ControlledPerimetreAutoCompleteProps<
  T extends FieldValues,
  Name extends FieldPath<T>,
> extends ControlledInput<T, Name> {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultHelperText?: string;
}

export function ControlledPerimetreAutoComplete<T extends FieldValues, Name extends FieldPath<T>>({
  name,
  control,
  label,
  placeholder,
  defaultValue,
  rules,
  disabled = false,
  defaultHelperText,
}: Readonly<ControlledPerimetreAutoCompleteProps<T, Name>>): ReactNode {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control, rules, defaultValue });

  return (
    <FormControl fullWidth>
      <PerimetreAutoCompleteWithErrorBoundary
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {(error?.message ?? defaultHelperText) != null && (
        <FormHelperText error={error != null}>{error?.message ?? defaultHelperText}</FormHelperText>
      )}
    </FormControl>
  );
}
